import React, { useEffect, useState } from "react";
import { PageHeader } from "./Pengumuman";
import { UserCog, Plus, Trash2, Edit3, Search } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const emptyForm = { name: "", email: "", password: "", role: "warga", phone: "", blok: "", status: "active" };

const UserManagement = () => {
  const { users, refreshUsers, addUser, updateUser, deleteUser } = useApp();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { refreshUsers(); }, []);

  const filtered = users.filter((u) => (u.name + u.email + (u.blok || "")).toLowerCase().includes(q.toLowerCase()));
  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, phone: u.phone || "", blok: u.blok || "", status: u.status }); setOpen(true); };

  const submit = async () => {
    if (!form.name || !form.email || (!editing && !form.password)) { toast.error("Nama, email, password wajib"); return; }
    try {
      if (editing) {
        const patch = { ...form };
        if (!patch.password) delete patch.password;
        await updateUser(editing.id, patch);
        toast.success("Pengguna diperbarui");
      } else {
        await addUser(form);
        toast.success("Pengguna ditambahkan");
      }
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const remove = async (id) => { try { await deleteUser(id); toast.success("Pengguna dihapus"); } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } };
  const badgeClass = (r) => r === "super_admin" ? "bg-purple-100 text-purple-800" : r === "admin" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800";

  return (
    <div>
      <PageHeader title="Kelola Pengguna" subtitle="Manajemen akun warga, admin, dan super admin" icon={UserCog}
        action={<Dialog open={open} onOpenChange={setOpen}><DialogTrigger asChild><Button className="brand-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1" />Tambah Pengguna</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>{editing ? "Edit Pengguna" : "Tambah Pengguna"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nama Lengkap</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>{editing ? "Password (kosongkan jika tidak diubah)" : "Password"}</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Role</Label><Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="warga">Warga</SelectItem><SelectItem value="admin">Admin RT</SelectItem><SelectItem value="super_admin">Super Admin</SelectItem></SelectContent></Select></div>
                <div><Label>Status</Label><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="pending">Menunggu</SelectItem><SelectItem value="suspended">Suspended</SelectItem></SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>No. HP</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Blok</Label><Input value={form.blok} onChange={(e) => setForm({ ...form, blok: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={submit} className="brand-btn">{editing ? "Simpan" : "Tambah"}</Button></DialogFooter>
          </DialogContent></Dialog>}
      />
      <div className="relative mb-4 max-w-md"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Cari nama / email / blok..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      <Card><CardContent className="p-0"><div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase"><tr>
          <th className="text-left p-3">Nama</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-left p-3">Blok</th><th className="text-center p-3">Status</th><th className="text-right p-3">Aksi</th>
        </tr></thead><tbody>
          {filtered.map((u) => (
            <tr key={u.id} className="border-t border-slate-100 hover:bg-emerald-50/30">
              <td className="p-3 font-medium text-slate-800">{u.name}</td><td className="p-3 text-slate-600">{u.email}</td>
              <td className="p-3"><Badge className={`${badgeClass(u.role)} hover:${badgeClass(u.role)} capitalize`}>{u.role.replace("_", " ")}</Badge></td>
              <td className="p-3">{u.blok}</td>
              <td className="p-3 text-center"><Badge variant="secondary" className={u.status === "active" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>{u.status}</Badge></td>
              <td className="p-3 text-right">
                <Button size="sm" variant="ghost" onClick={() => openEdit(u)}><Edit3 className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(u.id)} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
              </td>
            </tr>
          ))}
        </tbody></table></div></CardContent></Card>
    </div>
  );
};
export default UserManagement;
